import React, { useEffect, useMemo, useState } from "react"

import { BrowserRouter as Router } from "react-router-dom"
import { Provider } from "urql"

import { WithLoadingIndicator } from "~tailwindui/Basics"

import EventEmitter from "src/EventEmitter"
import { tutorClient as client } from "src/urql-client"

import { gql, useQuery } from "hooks/urql"
import useWebsocket from "hooks/useWebsocket"

import AppBody from "./AppBody"
import TutorContext from "./TutorContext"

const TutorApp = () => {
  const [result] = useQuery({ query, client })

  const [unreadMessageCount, setUnreadMessageCount] = useState<number>(0)

  useEffect(() => {
    if (!result.data) return
    setUnreadMessageCount(result.data.viewer.unreadMessageCount)
  }, [result.data])

  const channelProps = useMemo(() => {
    if (!result.data?.viewer?.id) return
    const viewerId = result.data.viewer.id
    return { id: viewerId }
  }, [result.data?.viewer?.id])

  useWebsocket({
    channelName: "MessageUserChannel",
    channelProps,
  })

  useEffect(() => {
    const handleEvent = data => {
      setUnreadMessageCount(data.count)
    }
    EventEmitter.subscribe("messageReceived", handleEvent)
    EventEmitter.subscribe("messageRead", handleEvent)

    return () => {
      EventEmitter.unsubscribe("messageReceived", handleEvent)
      EventEmitter.unsubscribe("messageRead", handleEvent)
    }
  }, [])

  return (
    <WithLoadingIndicator result={result}>
      {({ data: { viewer } }) => (
        <div className="min-h-screen bg-slate-100">
          <Provider value={client}>
            <TutorContext.Provider value={{ viewer, unreadMessageCount }}>
              <Router basename="/tutor">
                <AppBody />
              </Router>
            </TutorContext.Provider>
          </Provider>
        </div>
      )}
    </WithLoadingIndicator>
  )
}

const query = gql`
  query TutorContext {
    viewer {
      id
      firstName
      fullName
      email
      ssoEmail
      createdAt
      mayCreateExpense
      calendarId
      billDotComVendorId
      canSubmitGeneralExpenses
      hasSubmittedFingerprintExpense
      hasSubmittedIntroSessionExpense
      hasUnbilledSessions
      unreadMessageCount
      messagingDisabled
      avatar {
        thumbUrl
      }
    }
  }
`

export default TutorApp
